/**
 * Application Service
 * Handles tenant application CRUD operations and workflow
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { getFirebaseDB, getFirebaseStorage } from "@/lib/firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  TenantApplication,
  ApplicationDocument,
  ApplicationStatus,
} from "@/types/types";

/**
 * Create a new application
 */
export async function createApplication(
  applicationData: Omit<TenantApplication, "id" | "createdAt" | "updatedAt">
): Promise<string> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationsRef = collection(db, "applications");
  const newApplicationRef = doc(applicationsRef);

  const application: TenantApplication = {
    ...applicationData,
    id: newApplicationRef.id,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await setDoc(newApplicationRef, {
    ...application,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return newApplicationRef.id;
}

/**
 * Get application by ID
 */
export async function getApplicationById(
  applicationId: string
): Promise<TenantApplication | null> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationRef = doc(db, "applications", applicationId);
  const applicationSnap = await getDoc(applicationRef);

  if (!applicationSnap.exists()) {
    return null;
  }

  const data = applicationSnap.data();
  return {
    ...data,
    id: applicationSnap.id,
    createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
    submittedAt:
      data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt,
    reviewedAt: data.reviewedAt?.toDate?.()?.toISOString() || data.reviewedAt,
  } as TenantApplication;
}

/**
 * Get all applications for a tenant
 */
export async function getTenantApplications(
  tenantId: string
): Promise<TenantApplication[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationsRef = collection(db, "applications");
  const q = query(
    applicationsRef,
    where("tenantId", "==", tenantId),
    orderBy("createdAt", "desc")
  );

  const querySnapshot = await getDocs(q);
  const applications: TenantApplication[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    applications.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      submittedAt:
        data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt,
      reviewedAt: data.reviewedAt?.toDate?.()?.toISOString() || data.reviewedAt,
    } as TenantApplication);
  });

  return applications;
}

/**
 * Get all applications for a landlord's properties
 */
export async function getLandlordApplications(
  landlordId: string
): Promise<TenantApplication[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationsRef = collection(db, "applications");
  const q = query(
    applicationsRef,
    where("landlordId", "==", landlordId),
    orderBy("createdAt", "desc")
  );

  const querySnapshot = await getDocs(q);
  const applications: TenantApplication[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    applications.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      submittedAt:
        data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt,
      reviewedAt: data.reviewedAt?.toDate?.()?.toISOString() || data.reviewedAt,
    } as TenantApplication);
  });

  return applications;
}

/**
 * Get applications by property ID
 */
export async function getPropertyApplications(
  propertyId: string
): Promise<TenantApplication[]> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationsRef = collection(db, "applications");
  const q = query(
    applicationsRef,
    where("propertyId", "==", propertyId),
    orderBy("createdAt", "desc")
  );

  const querySnapshot = await getDocs(q);
  const applications: TenantApplication[] = [];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    applications.push({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
      submittedAt:
        data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt,
      reviewedAt: data.reviewedAt?.toDate?.()?.toISOString() || data.reviewedAt,
    } as TenantApplication);
  });

  return applications;
}

/**
 * Update application status
 */
export async function updateApplicationStatus(
  applicationId: string,
  status: ApplicationStatus,
  reviewData?: {
    reviewedBy: string;
    landlordNotes?: string;
    rejectionReason?: string;
  }
): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationRef = doc(db, "applications", applicationId);

  const updateData: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp(),
  };

  if (reviewData) {
    updateData.reviewedAt = serverTimestamp();
    updateData.reviewedBy = reviewData.reviewedBy;
    if (reviewData.landlordNotes) {
      updateData.landlordNotes = reviewData.landlordNotes;
    }
    if (reviewData.rejectionReason) {
      updateData.rejectionReason = reviewData.rejectionReason;
    }
  }

  if (status === "submitted") {
    updateData.submittedAt = serverTimestamp();
  }

  await updateDoc(applicationRef, updateData);
  return true;
}

/**
 * Update application
 */
export async function updateApplication(
  applicationId: string,
  updates: Partial<TenantApplication>
): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationRef = doc(db, "applications", applicationId);

  await updateDoc(applicationRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  return true;
}

/**
 * Upload application document
 */
export async function uploadApplicationDocument(
  applicationId: string,
  file: File,
  documentType: string
): Promise<ApplicationDocument> {
  const storage = getFirebaseStorage();
  if (!storage) throw new Error("Firebase Storage not initialized");

  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    throw new Error("File size exceeds 5MB limit");
  }

  // Generate unique filename
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 9);
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
  const filename = `${timestamp}_${randomId}_${sanitizedName}`;

  // Upload to Firebase Storage
  const storageRef = ref(
    storage,
    `applications/${applicationId}/documents/${filename}`
  );
  await uploadBytes(storageRef, file);

  // Get download URL
  const downloadURL = await getDownloadURL(storageRef);

  const document: ApplicationDocument = {
    id: randomId,
    name: file.name,
    type: documentType,
    url: downloadURL,
    uploadDate: new Date().toISOString(),
  };

  return document;
}

/**
 * Delete application document
 */
export async function deleteApplicationDocument(
  _applicationId: string,
  documentUrl: string
): Promise<boolean> {
  const storage = getFirebaseStorage();
  if (!storage) throw new Error("Firebase Storage not initialized");

  try {
    const storageRef = ref(storage, documentUrl);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    console.error("Error deleting document:", error);
    return false;
  }
}

/**
 * Delete application
 */
export async function deleteApplication(
  applicationId: string
): Promise<boolean> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationRef = doc(db, "applications", applicationId);
  await deleteDoc(applicationRef);
  return true;
}

/**
 * Get pending applications count for landlord
 */
export async function getPendingApplicationsCount(
  landlordId: string
): Promise<number> {
  const db = getFirebaseDB();
  if (!db) throw new Error("Firebase not initialized");

  const applicationsRef = collection(db, "applications");
  const q = query(
    applicationsRef,
    where("landlordId", "==", landlordId),
    where("status", "in", ["submitted", "under-review"])
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.size;
}

/**
 * Approve application and prepare for lease creation
 */
export async function approveApplication(
  applicationId: string,
  reviewedBy: string,
  landlordNotes?: string
): Promise<boolean> {
  return await updateApplicationStatus(applicationId, "approved", {
    reviewedBy,
    landlordNotes,
  });
}

/**
 * Reject application
 */
export async function rejectApplication(
  applicationId: string,
  reviewedBy: string,
  rejectionReason: string
): Promise<boolean> {
  return await updateApplicationStatus(applicationId, "rejected", {
    reviewedBy,
    rejectionReason,
  });
}
